import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import ProgressButton from '@/components/common/progress-button';

export interface ApplicationDataType {
  id: number;
  userId: number;
  userName: string;
  avatarUrl: string;
  remark?: string;
  createTime: number;
  status: 'pending' | 'accepted' | 'rejected';
}

interface Props {
  application: ApplicationDataType;
  isLoading?: boolean;
  onRespond?: (value: 'accept' | 'reject') => unknown | Promise<unknown>;
}

export function ApplicationListItem({ application, isLoading = false, onRespond }: Props) {
  const { t } = useTranslation();

  let buttons: ReactNode = null;

  if (isLoading) {
    buttons = (
      <ProgressButton
        as='button'
        isLoading
        className='btn btn-primary btn-md'
        label=''
      />
    );
  } else if (typeof onRespond === 'function') {
    buttons = (
      <>
        <ProgressButton
          as='button'
          isLoading={isLoading}
          className='btn btn-error btn-md'
          label='common.reject'
          onClick={() => onRespond('reject')}
        />
        <ProgressButton
          as='button'
          isLoading={isLoading}
          className='btn btn-primary btn-md'
          label='common.accept'
          onClick={() => onRespond('accept')}
        />
      </>
    );
  } else if (application.status !== 'pending') {
    buttons = (
      <button
        type='button'
        disabled
        className='btn btn-primary btn-md h-10 min-h-10'>
        {application.status === 'accepted' ? t('common.accept') : t('common.reject')}
      </button>
    );
  }

  return (
    <div className='grid grid-cols-[2.5rem_minmax(6rem,1fr)_auto] auto-rows-auto items-center gap-x-4 gap-y-2'>
      <AvatarWithStatus
        className='size-12'
        imageUrl={application.avatarUrl}
        label={application.userName}
        size={[48, 48]}
      />

      <div className='flex flex-col items-stretch justify-center gap-1'>
        <h5 className='truncate break-all text-h2 desktop:text-sm'>{application.userName}</h5>

        <span className='text-xs text-secondary'>{dayjs(application.createTime).fromNow()}</span>
      </div>

      <div className='flex h-fit items-center gap-2'>{buttons}</div>

      {application.remark && (
        <div className='p-2 bg-neutral-50 rounded col-span-full'>
          <p className='text-body1 line-clamp-2 break-words'>{application.remark}</p>
        </div>
      )}
    </div>
  );
}
